import { FocusEvent, MouseEvent, useCallback, useRef } from "react";

interface Handlers {
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLInputElement>) => void;
  onMouseUp?: (event: MouseEvent<HTMLInputElement>) => void;
}

/**
 * Adds selecting the whole value on focus to the handlers a field already
 * has, so typing replaces the value.
 *
 * Selecting in onFocus alone is not enough for a click: the mouse up that
 * follows places the caret and collapses the selection again. Suppressing
 * that mouse up would also take away the ability to select part of the value
 * by dragging, so it is only suppressed for the click that brought the field
 * into focus.
 */
export const useSelectOnFocus = (
  enabled: boolean | undefined,
  handlers: Handlers
): Handlers => {
  const alreadyFocused = useRef(false);
  const { onFocus, onMouseDown, onMouseUp } = handlers;

  const selectOnFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      event.currentTarget.select();
      onFocus?.(event);
    },
    [onFocus]
  );

  const rememberFocus = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      alreadyFocused.current = document.activeElement === event.currentTarget;
      onMouseDown?.(event);
    },
    [onMouseDown]
  );

  const keepSelection = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (!alreadyFocused.current) {
        event.preventDefault();
      }
      onMouseUp?.(event);
    },
    [onMouseUp]
  );

  if (!enabled) {
    return handlers;
  }

  return {
    onFocus: selectOnFocus,
    onMouseDown: rememberFocus,
    onMouseUp: keepSelection,
  };
};
